// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a picture opened from a Konvo chat steps through every picture loaded in that chat, oldest
// first, not just the ones on its own message. Each item carries its message so reply, forward,
// favourite and delete in the viewer act on the right one.

import type {Message} from '@app/features/messaging/models/MessagingMessage';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {attachmentsToViewerItems, isViewerImageItem} from '@app/features/messaging/utils/MediaViewerItemUtils';
import type {MediaViewerItem} from '@app/features/ui/state/MediaViewer';
import {MessageAttachmentFlags} from '@konvo/constants/src/ChannelConstants';

/** The pictures a message shows inline: images and GIFs with a size, not expired or behind a spoiler. */
function messagePictures(message: Message): Array<MediaViewerItem> {
	const shown = message.attachments.filter(
		(attachment) =>
			attachment.content_type?.startsWith('image/') &&
			(attachment.width ?? 0) > 0 &&
			(attachment.height ?? 0) > 0 &&
			!attachment.expired &&
			(attachment.flags & MessageAttachmentFlags.IS_SPOILER) === 0,
	);
	return attachmentsToViewerItems(shown)
		.filter(isViewerImageItem)
		.map((item) => ({...item, message}));
}

export function withChatPictures(
	items: ReadonlyArray<MediaViewerItem>,
	currentIndex: number,
	message: Message | undefined,
): {items: ReadonlyArray<MediaViewerItem>; currentIndex: number} {
	const current = items[currentIndex];
	if (!message || !current || !isViewerImageItem(current)) return {items, currentIndex};
	const loaded = Messages.getMessages(message.channelId).toArray();
	const at = loaded.findIndex((candidate) => candidate.id === message.id);
	if (at < 0) return {items, currentIndex};
	// A picture opened on its own (not from a grid) still brings its message's other pictures.
	const ownPictures = messagePictures(message);
	const pictureIndex = ownPictures.findIndex((item) => item.attachmentId === current.attachmentId);
	const alone = items.length === 1 && current.attachmentId !== undefined && pictureIndex >= 0;
	const own = alone ? ownPictures : items.map((item) => ({...item, message}));
	const ownIndex = alone ? pictureIndex : currentIndex;
	const before = loaded.slice(0, at).flatMap(messagePictures);
	const after = loaded.slice(at + 1).flatMap(messagePictures);
	if (before.length === 0 && after.length === 0 && !alone) return {items, currentIndex};
	return {items: [...before, ...own, ...after], currentIndex: before.length + ownIndex};
}
